import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormGroup, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import api, { DiscontinueProductsResultDto, ImportColumnsSuggestionDto, ImportProductsArgsAction, ImportProductsResultDto, VendorDto } from "../../../api";
import { UploadedFileInfo, UploadFileControl } from "../../controls";
import { ColumnList } from "../../controls/ColumnList";
import { ColumnMultiList } from "../../controls/ColumnMultiList";
import { firstLetterUppercase, isExcel, notif } from "../../utils";
import { ImportActionList } from "./ImportActionList";

export type VendorProductsDialogAction = "none" | "import" | "discontinue";

export interface VendorProductsDialogProps {
  action: VendorProductsDialogAction;
  vendor: VendorDto | null;
  onClose?: () => void;
  onImport?: (result: ImportProductsResultDto) => void;
  onDiscontinue?: (result: DiscontinueProductsResultDto) => void;
}

export const VendorProductsDialog = (props: VendorProductsDialogProps) => {
  const { vendor, action, onClose, onImport, onDiscontinue } = props;

  const [open, setOpen] = useState(false);
  const [originalName, setOriginalName] = useState("");
  const [fileName, setFileName] = useState("");
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState("");
  const [headerRow, setHeaderRow] = useState("1");
  const [dataRow, setDataRow] = useState("2");
  const [columns, setColumns] = useState<string[]>([]);
  const [columnSKU, setColumnSKU] = useState("");
  const [columnQuantity, setColumnQuantity] = useState("");
  const [tokenColumns, setTokenColumns] = useState<string[]>([]);
  const [importAction, setImportAction] = useState<ImportProductsArgsAction>(ImportProductsArgsAction.None);

  const handleAccept = async () => {
    try {
      if (vendor?.id && fileName) {
        if (action === "import") {
          const result = await api.vendor.importProducts({
            fileName: fileName,
            vendorId: vendor.id,
            columnSKU: columnSKU,
            columnQuantity: columnQuantity,
            tokenColumns: tokenColumns,
            sheet,
            headerRow,
            dataRow,
            action: importAction,
          } as any);
          setOpen(false);
          if (onImport) onImport(result);
        } else if (action === "discontinue") {
          const result = await api.vendor.discontinueProducts({
            fileName: fileName,
            vendorId: vendor.id,
            columnSKU: columnSKU,
            sheet,
            headerRow,
            dataRow,
          } as any);
          setOpen(false);
          if (onDiscontinue) onDiscontinue(result);
        } else {
          notif(`Unsupported action ${action}`, "error");
        }
      } else {
        notif("Vendor not selected.", "error");
      }
    } catch (err) {
      notif(err, "error");
    }
  };
  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const loadColumns = useCallback(async () => {
    if (fileName) {
      try {
        let result: string[] = [];
        let suggestion: ImportColumnsSuggestionDto;
        if (isExcel(fileName) && sheet && headerRow) {
          result = await api.file.columns(fileName, sheet, parseInt(headerRow));
          suggestion = await api.file.suggestion(fileName, sheet, parseInt(headerRow));
        } else {
          result = await api.file.columns(fileName, undefined, undefined);
          suggestion = await api.file.suggestion(fileName, undefined, undefined);
        }
        setColumnSKU(suggestion?.columnSKU || "");
        setColumnQuantity(suggestion?.columnQuantity || "");
        setTokenColumns(suggestion?.tokenColumns || []);
        setColumns(result);
      } catch (err) {
        // notif(err, "error");
      }
    }
  }, [sheet, headerRow, fileName]);

  const handleUpload = async (info: UploadedFileInfo) => {
    setOriginalName(info.originalname);
    setFileName(info.name);
    setSheet("");
    setHeaderRow("1");
    setDataRow("2");
    setColumnSKU("");
    setColumnQuantity("");
    setTokenColumns([]);
    setImportAction(ImportProductsArgsAction.None);
    if (isExcel(info.extension)) {
      const result = await api.file.sheets(info.name);
      setSheets(result);
    } else {
      setSheets([]);
    }
  };

  useEffect(() => {
    if (action !== "none" && vendor !== null) {
      setOriginalName("<none>");
      setFileName("");
      setSheet("");
      setHeaderRow("1");
      setSheets([]);
      setColumns([]);
      setColumnSKU("");
      setColumnQuantity("");
      setTokenColumns([]);
      setImportAction(ImportProductsArgsAction.None);
      setOpen(true);
    }
  }, [action, vendor]);

  useEffect(() => {
    loadColumns();
  }, [sheet, headerRow, fileName]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl">
      <DialogTitle>{action.toUpperCase()} PRODUCTS</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} direction="row" alignItems="top">
          <FormGroup>
            <Typography>Vendor: {vendor?.name}</Typography>
            <Divider sx={{ paddingTop: 10 }} />
            <FormControl component="fieldset" sx={{ paddingTop: 10 }}>
              <FormGroup>
                <Stack spacing={2} direction="row" alignItems="center">
                  <Typography>File:</Typography>
                  <Typography>{originalName}</Typography>
                  <UploadFileControl accept={["csv", "xls", "xlsx"]} onUploaded={(e) => handleUpload(e)} />
                </Stack>
              </FormGroup>
            </FormControl>
            {sheets.length > 0 && (
              <FormGroup>
                <br />
                <FormControl>
                  <InputLabel id="sheet-select-label">Sheet</InputLabel>
                  <Select labelId="sheet-select-label" label="Sheet" value={sheet} onChange={(e) => setSheet(e.target.value)}>
                    {sheets?.map((sheet, index) => {
                      return (
                        <MenuItem key={index} value={sheet}>
                          {sheet}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField label="Header row" variant="standard" type="number" value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} />
                <TextField label="Data row" variant="standard" type="number" value={dataRow} onChange={(e) => setDataRow(e.target.value)} />
              </FormGroup>
            )}
            {columns.length > 0 && (
              <FormGroup>
                <FormGroup sx={{ paddingTop: 6 }}>
                  <ColumnList label="Column SKU" columns={columns} value={columnSKU} onChange={(value) => setColumnSKU(value)} />
                </FormGroup>
                {action === "import" && (
                  <FormGroup sx={{ paddingTop: 6 }}>
                    <ColumnList label="Column Quantity" columns={columns} value={columnQuantity} onChange={(value) => setColumnQuantity(value)} />
                  </FormGroup>
                )}
              </FormGroup>
            )}
            {action === "import" && (
              <FormControl component="fieldset" sx={{ paddingTop: 10 }}>
                <FormGroup>
                  <Divider sx={{ paddingTop: 10 }} />
                  <ImportActionList value={importAction} onChange={(value) => setImportAction(value)} />
                </FormGroup>
              </FormControl>
            )}
          </FormGroup>
          {action === "import" && columns.length > 0 && <ColumnMultiList columns={columns} values={tokenColumns} onChange={(values) => setTokenColumns(values)} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAccept}>{firstLetterUppercase(action)}</Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
